/**
 * `<policy-envelope>` — the shared envelope every kind carries (`SC-F502`,
 * `SC-S502`, and the mode half of `SC-F527`).
 *
 * The one thing this element must never do is offer `apiVersion`. The registry binds
 * each kind to exactly one GroupVersion, and a mismatched pair is an **unknown-kind
 * decode error** that fails the whole bundle — so the value is derived from the kind
 * and shown as a fact, never as an input. Offering the choice would only create a
 * way to be wrong.
 *
 * Three further decisions are visible here:
 *
 *   - **Priority is a number with an ordering preview.** The preview lists this
 *     object's neighbours in snapshot order — priority descending, then name
 *     ascending — because the tie-break by *name* decides real outcomes and nothing
 *     about a number field says so.
 *   - **A new policy starts in `Audit`.** Sluice assumes `Enforce` when the field is
 *     absent, so the schema's `initial` differs from its `default` on purpose: a
 *     policy nobody has yet reasoned about against their own schema should be
 *     observable before it decides anything.
 *   - **`namespace`, `labels` and `annotations` are marked inert, not hidden in
 *     silence.** They decode and are read by nothing in Sluice. They are preserved
 *     on import so a round trip is lossless, and the form says so rather than
 *     letting a reader conclude they group something.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import { enforcementModeVariant } from "../../components/index.js";
import type { ObjectMeta, PolicyFinding, PolicyKind } from "../../api/policies.js";
import { diagnosticSeverity, findingsAt, worst } from "./policy-findings.js";
import type { PolicySchema, SchemaField } from "./schema.js";

/** Detail of `envelope-change`. `mode` is `spec.enforcementMode`, which lives on
 * the spec rather than on the metadata but belongs in the same header. */
export interface EnvelopeChangeDetail {
  metadata: ObjectMeta;
  mode: string;
}

/** OrderPeer is one same-kind sibling, for the ordering preview. */
export interface OrderPeer {
  name: string;
  priority: number;
  shadow?: boolean;
}

@customElement("policy-envelope")
export class PolicyEnvelope extends LitElement {
  @property({ attribute: false }) schema!: PolicySchema;
  @property() kind: PolicyKind | "" = "";
  @property({ attribute: false }) metadata: ObjectMeta = { name: "" };
  /** `spec.enforcementMode` as authored — the empty string means "absent", which
   * Sluice reads as `Enforce`. */
  @property() mode = "";
  /** True while creating, when the name is still editable. A rename is semantically
   * a delete plus a create — it changes every tie-break that sorts by name — so the
   * editor does not offer it as an edit. */
  @property({ type: Boolean }) create = false;
  /** Whether this kind carries `spec.enforcementMode` at all. */
  @property({ type: Boolean }) enforcement = false;
  /** Same-kind siblings, for the ordering preview. */
  @property({ attribute: false }) peers: OrderPeer[] = [];
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  /** False for an object soft-deleted out of the render, so the header can say so. */
  @property({ type: Boolean }) enabled = true;
  /**
   * True once the object exists, when the mode may not be changed by a
   * whole-document save.
   *
   * Leaving a shadow mode starts changing real query outcomes, and the API gates
   * that on an explicit acknowledgement carrying the observation evidence — but it
   * gates the **envelope patch**, not the document replace. So this surface routes
   * every mode change through the acknowledged path rather than letting a save that
   * was about a table pattern quietly promote a policy to Enforce.
   */
  @property({ type: Boolean, attribute: "mode-locked" }) modeLocked = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
    }
    .row > * {
      flex: 1 1 14rem;
      min-width: 0;
    }
    bn-field {
      margin-bottom: var(--bino-space-md);
    }
    .derived {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0 0 var(--bino-space-md);
      max-width: 80ch;
    }
    .derived code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
    }
    fieldset {
      border: none;
      margin: 0 0 var(--bino-space-sm);
      padding: 0;
    }
    legend {
      font-size: var(--bino-font-size-base);
      font-weight: 500;
      color: var(--bino-text-muted);
      padding: 0;
      margin-bottom: 0.375rem;
    }
    .segmented {
      display: inline-flex;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      overflow: hidden;
      background: var(--bino-surface-subtle);
    }
    .segmented label {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: 0.375rem 0.75rem;
      font-size: var(--bino-font-size-md);
      cursor: pointer;
      border-right: 1px solid var(--bino-border-light);
    }
    .segmented label:last-child {
      border-right: none;
    }
    .segmented label.on {
      background: var(--bino-surface-active);
      color: var(--bino-active-text);
      font-weight: 600;
    }
    .segmented label.off {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .segmented input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      pointer-events: none;
    }
    .segmented label:has(input:focus-visible) {
      box-shadow: var(--bino-focus-ring-shadow-inset);
    }
    .locked {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .lockednote {
      flex: 1 1 24rem;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .modes {
      display: grid;
      gap: var(--bino-space-xs);
      margin: var(--bino-space-sm) 0 var(--bino-space-md);
    }
    .modes p {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .order {
      margin: 0 0 var(--bino-space-md);
    }
    .order ol {
      list-style: none;
      counter-reset: pos;
      margin: var(--bino-space-xs) 0 0;
      padding: 0;
      display: grid;
      gap: 0.125rem;
    }
    .order li {
      counter-increment: pos;
      display: flex;
      gap: var(--bino-space-sm);
      align-items: baseline;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .order li::before {
      content: counter(pos) ".";
      font-family: var(--bino-font-mono);
      color: var(--bino-text-secondary);
    }
    .order li.self {
      color: var(--bino-text);
      font-weight: 600;
    }
    .order .name {
      font-family: var(--bino-font-mono);
    }
    .inert {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
      max-width: 80ch;
    }
  `;

  private emit(patch: { metadata?: Partial<ObjectMeta>; mode?: string }) {
    this.dispatchEvent(
      new CustomEvent<EnvelopeChangeDetail>("envelope-change", {
        detail: {
          metadata: { ...this.metadata, ...(patch.metadata ?? {}) },
          mode: patch.mode ?? this.mode,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** The envelope group for this kind: the enforcement kinds order by priority, the
   * other four do not and the schema hides the field for them. */
  private get group(): string {
    return this.enforcement ? "objectMetaEnforcement" : "objectMetaInert";
  }

  private field(name: string): SchemaField | undefined {
    return this.schema.field(this.group, name);
  }

  /** The local name fault, stated instantly. The 63-character bound is **ours** —
   * the upstream regex has no length limit — so it is a linter rule rather than a
   * DSL rule, and the message says which. */
  private nameFault(): string {
    const field = this.field("name");
    const name = this.metadata.name ?? "";
    if (name === "") return "";
    const pattern = field?.pattern;
    if (pattern && !new RegExp(pattern).test(name)) {
      return `A name must match ${pattern}: lowercase letters, digits and dashes, starting and ending alphanumeric.`;
    }
    const max = field?.maxLength ?? 0;
    if (max > 0 && name.length > max) {
      return `At most ${max} characters. Sluice's own regex has no length bound, so this limit is enforced here and by the linter rather than by the engine.`;
    }
    return "";
  }

  private priorityFault(): string {
    const field = this.field("priority");
    const value = this.metadata.priority;
    if (value === undefined) return "";
    const min = field?.min ?? 0;
    const max = field?.max ?? 1000;
    if (!Number.isInteger(value) || value < min || value > max) {
      return `Priority must be a whole number between ${min} and ${max}.`;
    }
    return "";
  }

  /** apiVersion, as a fact. */
  private renderDerived() {
    const kindSchema = this.kind ? this.schema.kind(this.kind) : undefined;
    const apiVersion = kindSchema?.apiVersion ?? "";
    return html`<p class="derived">
      <code>apiVersion</code> is
      <code>${apiVersion || "derived from the kind"}</code>, derived from
      <code>${this.kind || "the kind"}</code> and never an input: each kind is registered under
      exactly one version, so a mismatched pair is an unknown-kind decode error that fails the whole
      bundle.
    </p>`;
  }

  /** The segmented enforcement-mode control. A value the pinned build treats as
   * identical to another is selectable and badged — `DryRun` is behaviourally
   * `Audit`, and the control says so rather than implying a third behaviour. */
  private renderMode() {
    if (!this.enforcement) return nothing;
    const field = this.schema.field(this.modeGroup, "enforcementMode");
    const options = this.schema.options(field?.enum ?? "EnforcementMode");
    const fallbackDefault = this.schema.enumDefault(field?.enum ?? "EnforcementMode");
    const current = this.mode || fallbackDefault;
    return html`
      <fieldset>
        <legend>Enforcement mode</legend>
        ${this.modeLocked
          ? html`<div class="locked">
              <bn-badge .variant=${enforcementModeVariant(this.enabled ? current : "Disabled")}>
                ${this.enabled ? current : "Disabled"}
              </bn-badge>
              <bn-button
                size="sm"
                @click=${() =>
                  this.dispatchEvent(
                    new CustomEvent("envelope-promote", { bubbles: true, composed: true }),
                  )}
              >
                Change enforcement mode
              </bn-button>
              <span class="lockednote"
                >Leaving a shadow mode starts changing real query outcomes, so it is an acknowledged
                action of its own rather than a side effect of saving this form.</span
              >
            </div>`
          : html`<div class="segmented" role="radiogroup" aria-label="Enforcement mode">
              ${options.map(
                (o) => html`<label
                  class=${o.value === current ? "on" : o.disabled ? "off" : ""}
                  title=${o.disabledReason ?? ""}
                >
                  <input
                    type="radio"
                    name="enforcement-mode"
                    .checked=${o.value === current}
                    ?disabled=${!!o.disabled}
                    @change=${() => this.emit({ mode: o.value })}
                  />
                  ${o.label}
                </label>`,
              )}
            </div>`}
      </fieldset>
      <div class="modes">
        <bn-badge .variant=${enforcementModeVariant(this.enabled ? current : "Disabled")}>
          ${this.enabled ? current : "Disabled"}
        </bn-badge>
        ${options
          .filter((o) => o.value === current)
          .map((o) => html`<p>${o.description}</p>`)}
        ${this.enabled
          ? nothing
          : html`<p>
              This object is in the generation and out of the render, so nothing about it reaches the
              instance whatever its mode says.
            </p>`}
      </div>
    `;
  }

  /** Which schema group carries `enforcementMode`. `policyCommon` holds it for six
   * kinds; `SqlAccessPolicy` declares it on its own spec group because its field order
   * differs. Both are asked, and the first that answers wins. */
  private get modeGroup(): string {
    return this.schema.field("policyCommon", "enforcementMode") ? "policyCommon" : "sqlAccessSpec";
  }

  /**
   * The ordering preview. It is computed locally from the sibling list so it moves as
   * the author types, and it is explicitly the *advisory* view of the order the
   * server reports on save.
   */
  private renderOrder() {
    if (!this.enforcement) return nothing;
    const name = this.metadata.name || "(this policy)";
    const priority = this.metadata.priority ?? 0;
    const rows: Array<OrderPeer & { self?: boolean }> = [
      ...this.peers.filter((p) => p.name !== this.metadata.name),
      { name, priority, self: true },
    ].sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name));
    if (rows.length === 1) {
      return html`<div class="order">
        <p class="derived">
          This is the only ${this.kind} in the draft, so nothing competes with it yet. Order is
          priority descending, then name ascending.
        </p>
      </div>`;
    }
    return html`<div class="order">
      <p class="derived">
        Evaluation order for ${this.kind}: priority descending, then <strong>name ascending</strong> —
        two policies at the same priority are ordered alphabetically, and that tie-break decides real
        outcomes.
      </p>
      <ol>
        ${rows.map(
          (r) => html`<li class=${r.self ? "self" : ""}>
            <span class="name">${r.name}</span>
            <span>priority ${r.priority}</span>
            ${r.shadow ? html`<bn-badge variant="audit">shadow</bn-badge>` : nothing}
          </li>`,
        )}
      </ol>
    </div>`;
  }

  override render() {
    if (!this.schema) return nothing;
    const nameField = this.field("name");
    const priorityField = this.field("priority");
    const nameFinding = worst(findingsAt(this.findings, "metadata.name"));
    const nameFault = this.nameFault();
    const priorityFault = this.priorityFault();
    const priorityHidden = this.schema.offer(priorityField) === "hidden";

    return html`
      ${this.renderDerived()}
      <div class="row">
        <bn-field
          label="Name"
          required
          hint=${nameField?.help ?? ""}
          error=${nameFault || (nameFinding?.message ?? "")}
          rule=${nameFault ? "" : (nameFinding?.rule ?? "")}
          severity=${nameFault ? "error" : nameFinding ? diagnosticSeverity(nameFinding.severity) : "error"}
        >
          <bn-input
            mono
            .value=${this.metadata.name ?? ""}
            ?disabled=${!this.create}
            @input=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.emit({ metadata: { name: e.detail.value } });
            }}
          ></bn-input>
        </bn-field>
        ${priorityHidden
          ? nothing
          : html`<bn-field
              label="Priority"
              hint=${priorityField?.help ?? ""}
              error=${priorityFault}
            >
              <bn-input
                type="number"
                inputmode="numeric"
                .value=${String(this.metadata.priority ?? priorityField?.default ?? 0)}
                @input=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  const parsed = Number.parseInt(e.detail.value, 10);
                  this.emit({
                    metadata: { priority: Number.isNaN(parsed) ? 0 : parsed },
                  });
                }}
              ></bn-input>
            </bn-field>`}
      </div>
      ${this.create
        ? nothing
        : html`<p class="derived">
            The name is fixed after creation: renaming an object is semantically a delete plus a
            create, because it changes every conflict-resolution tie-break that sorts by name.
          </p>`}
      ${this.renderMode()} ${this.renderOrder()}
      <p class="inert">
        <code>metadata.namespace</code>, <code>labels</code> and <code>annotations</code> decode and
        are read by nothing in Sluice — they are not part of the uniqueness key and they group
        nothing. Values already on this object are preserved so a round trip is lossless, and the form
        does not offer them.
      </p>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-envelope": PolicyEnvelope;
  }
}
